/**
 * Feature key under which the location-tracker slice is registered in the root store.
 */
export const LOCATION_TRACKER_FEATURE_KEY = 'locationTracker';

/**
 * User-store key holding the user's editable share/privacy settings (see
 * `LocationTrackerUserConfig` in `location-tracker.wire.ts`). The user-store slice is the single
 * source of truth for this key — this slice only keeps a working draft, seeded from it.
 */
export const LOCATION_TRACKER_STORE_KEY = 'LocationTracker';

/**
 * User-store key holding the derived work-day timestamps the companion iOS app reads (see
 * `LocationTrackerTrackingState` in `location-tracker.wire.ts`).
 */
export const LOCATION_TRACKER_STATE_STORE_KEY = 'LocationTrackerState';

/** Truncating coordinates to 3 decimals blurs the position by roughly 111 m. */
export const PRIVACY_PERIMETER_PRECISION_FACTOR = 1000;

/** How long the save confirmation or error stays on screen. */
export const SAVE_MESSAGE_VISIBLE_MS = 5000;

/**
 * Lifecycle of a save request, mirroring the old component's `isSaving` flag plus whatever
 * caused the last save message to be shown.
 */
export enum LocationTrackerSaveStatus {
    Idle = 'idle',
    Saving = 'saving',
    Saved = 'saved',
    Failed = 'failed',
}

/**
 * Translation keys for a failed geolocation lookup, moved verbatim from
 * `LocationTrackerComponent.selectLocation`'s `GeolocationPositionError.code` mapping.
 */
export enum LocationTrackerErrorKey {
    NotSupported = 'LOCATION_TRACKER_ERROR_NOT_SUPPORTED',
    PermissionDenied = 'LOCATION_TRACKER_ERROR_PERMISSION_DENIED',
    PositionUnavailable = 'LOCATION_TRACKER_ERROR_POSITION_UNAVAILABLE',
    Timeout = 'LOCATION_TRACKER_ERROR_TIMEOUT',
    Unknown = 'LOCATION_TRACKER_ERROR_UNKNOWN',
}

/** Translation keys for the save confirmation/error banner, moved verbatim from `LocationTrackerComponent.SaveMessageType`. */
export enum LocationTrackerSaveMessageKey {
    Success = 'LOCATION_TRACKER_SAVE_SUCCESS',
    Error = 'LOCATION_TRACKER_SAVE_ERROR',
}

/** The page's save banner: which message to show and whether it reads as an error. */
export type LocationTrackerSaveMessage = Readonly<{
    isError: boolean;
    key: LocationTrackerSaveMessageKey;
}>;

/**
 * Naked, serializable shape of {@link LocationTrackerUserConfig} (the wire type) — `undefined`
 * coordinates replace the wire format's `null`, matching the rest of the codebase.
 */
export type LocationTrackerUserConfigState = Readonly<{
    shareLocation: boolean;
    privacyPerimeterLatitude: number | undefined;
    privacyPerimeterLongitude: number | undefined;
    state: string;
    businessLine: string;
}>;

/**
 * The inputs every companion-push decision shares — see `selectCompanionPushGate`. Both push
 * events are suppressed while the user has not shared their location, and both wait for the user
 * store to settle first, so a mirror that has simply not loaded yet is never mistaken for
 * `shareLocation: false`.
 */
export type LocationTrackerCompanionPushGate = Readonly<{
    /** Whether the user store's backend GET has settled — see `selectUserStoreSettled`. */
    isReady: boolean;
    /** From the stored `LocationTracker` config. */
    shareLocation: boolean;
}>;

/**
 * {@link LocationTrackerCompanionPushGate} plus the start-of-work push's own dedupe marker — see
 * `selectCompanionStartOfWorkPushGate`. Bundled into one type because the effect that consumes it
 * needs all three fields to make a single decision; reading them as three separate selectors would
 * just recombine them at the call site.
 */
export type LocationTrackerCompanionStartOfWorkPushGate = LocationTrackerCompanionPushGate &
    Readonly<{
        /** From the stored `LocationTrackerState`; doubles as the push's dedupe marker. */
        storedStartOfWork: string | undefined;
    }>;

/**
 * The location-tracker page's state: the draft config being edited, the in-flight/outcome status
 * of the last save, and the geolocation lookup used to fill in the privacy perimeter.
 *
 * This is a pure page-state slice — no load status, no persistence, and no backend calls of its
 * own. Both storage keys it edits/derives live in the user-store slice, which is the single source
 * of truth; this slice only keeps a working draft and page-local UI state.
 */
export type LocationTrackerState = Readonly<{
    /** The config being edited. `undefined` until seeded from the user store — see the seed effect. */
    draft: LocationTrackerUserConfigState | undefined;
    saveStatus: LocationTrackerSaveStatus;
    isPerimeterRequestPending: boolean;
    perimeterErrorKey: LocationTrackerErrorKey | undefined;
}>;

/**
 * Default-entity factory — mirrors `LocationTrackerService.getDefaultConfig`. Used by
 * `userStoreValueToConfigState`'s fallback, so a missing/malformed stored value and the initial
 * state can never drift.
 */
export function createDefaultLocationTrackerUserConfigState(): LocationTrackerUserConfigState {
    return {
        shareLocation: false,
        privacyPerimeterLatitude: undefined,
        privacyPerimeterLongitude: undefined,
        state: '',
        businessLine: '',
    };
}

export const initialLocationTrackerState: LocationTrackerState = {
    draft: undefined,
    saveStatus: LocationTrackerSaveStatus.Idle,
    isPerimeterRequestPending: false,
    perimeterErrorKey: undefined,
};
